/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  cloneDeepWith,
  compact,
  findIndex,
  groupBy,
  isArray,
  isDate,
  isObject,
  last,
  map,
  mapValues,
  omitBy,
  pickBy,
  sortBy,
  toPairs,
  without,
} from 'lodash';

/**
 * Recursively removes all undefined properties
 */
export function omitDeepUndefined<T>(value: T): T {
  return omitDeep(value, [undefined]);
}

export function omitUndefined<T extends object>(value: T): T {
  return pickBy(value, (v) => v !== undefined) as T;
}
/**
 * Recursively removes all undefined properties
 */
export function omitDeepNil<T>(value: T): T {
  return omitDeep(value, [undefined, null]);
}

export function omitDeep<T>(value: T, omittedValues: any[]): T {
  if (isArray(value)) {
    const newArrayValues = map(value, (v) => omitDeep(v, omittedValues));
    return without(newArrayValues, ...omittedValues) as any;
  }

  if (isObject(value)) {
    const omittedValue = mapValues(value as any, (v) => omitDeep(v, omittedValues));
    return omitBy(omittedValue, (v) => omittedValues.includes(v)) as any;
  }

  return value;
}

export function cloneDeepWithOptions(
  obj: any,
  options: {
    replaceDateWithTimestamps?: boolean;
  }
) {
  return cloneDeepWith(obj, (value) => {
    if (options.replaceDateWithTimestamps && isDate(value)) {
      return value.getTime();
    }
    return undefined;
  });
}

export function itemAfter<T>(array: T[], predicate: (item: T) => boolean): T | undefined {
  const index = findIndex(array, predicate);
  return array[index + 1];
}

export function itemBefore<T>(array: T[], predicate: (item: T) => boolean): T | undefined {
  const index = findIndex(array, predicate);
  return array[index - 1];
}

export function insertAt<T>(array: T[], value: T, index: number): T[] {
  array.splice(index, 0, value);
  return array;
}

export function mostEncounteredValue(values: string[]): string | undefined {
  const grouped = groupBy(values);
  const pairs = toPairs(grouped);
  const sorted = sortBy(pairs, (pair) => pair[1].length);
  const pair = last(sorted);
  return pair && pair[0];
}

/** maps and removes all nulls and undefineds from the array */
export function compactMap<T, TResult>(
  collection: T[] | null | undefined,
  iteratee: (item: T) => TResult | undefined | null
): TResult[] {
  return compact(map(collection, iteratee));
}
